import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CheckIcon, ZapIcon, TargetIcon, TrendingUpIcon } from 'lucide-react';
const solutionPoints = ['Automatic lead capture from all channels', 'Intelligent follow-up sequences that never forget', 'Real-time pipeline with accurate revenue forecasts', 'Seamless integration with your existing tools', 'Complete activity history per contact', 'Automated reporting dashboards'];
const whyUs = [{
  icon: TargetIcon,
  title: 'Close CRM Specialists',
  description: 'We work exclusively with Close CRM. No distraction from other systems – 100% focus on your solution.'
}, {
  icon: ZapIcon,
  title: 'Implementation in 2-4 weeks',
  description: 'While other agencies take months, you will be live with us in a few weeks – with full support.'
}, {
  icon: TrendingUpIcon,
  title: '110+ successful projects',
  description: 'We have helped over 110 entrepreneurs transform their sales with Close CRM – with measurable results.'
}];
export function SolutionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 lg:py-32 bg-[#0c0c0c]" ref={ref} data-id="element-286">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-287">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-288">
          {/* Left side - Solution intro */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-289">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#0ea5e9]/10 text-[#0ea5e9] text-sm font-medium mb-6" data-id="element-290">
              The Solution
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-291">
              Close CRM – The sales system for{' '}
              <span className="text-[#0ea5e9]" data-id="element-292">winners</span>
            </h2>
            <p className="mt-6 text-lg text-gray-400 leading-relaxed" data-id="element-293">
              Close CRM was developed by salespeople for salespeople. It is the
              only CRM that your team will actually use – because it makes
              selling easier, not more complicated.
            </p>

            <div className="mt-8 space-y-4" data-id="element-294">
              {solutionPoints.map((point, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} animate={isInView ? {
              opacity: 1,
              x: 0
            } : {}} transition={{
              duration: 0.4,
              delay: 0.2 + index * 0.1
            }} className="flex items-start gap-3" data-id="element-295">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-296">
                    <CheckIcon className="w-4 h-4 text-emerald-400" data-id="element-297" />
                  </div>
                  <span className="text-gray-300" data-id="element-298">{point}</span>
                </motion.div>)}
            </div>
          </motion.div>

          {/* Right side - Why us */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="space-y-6" data-id="element-299">
            <h3 className="text-2xl font-bold text-white mb-8" data-id="element-300">
              Why Close CRM Agency?
            </h3>
            {whyUs.map((item, index) => <div key={index} className="p-6 rounded-2xl bg-[#1a1a1a] border border-white/5" data-id="element-301">
                <div className="flex items-start gap-4" data-id="element-302">
                  <div className="w-12 h-12 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0" data-id="element-303">
                    <item.icon className="w-6 h-6 text-[#0ea5e9]" data-id="element-304" />
                  </div>
                  <div data-id="element-305">
                    <h4 className="text-lg font-semibold text-white mb-2" data-id="element-306">
                      {item.title}
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-307">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="mt-16 text-center" data-id="element-308">
          <a href="/free-strategy-call" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0ea5e9] text-white font-semibold rounded-lg hover:bg-[#0284c7] transition-all duration-200 shadow-lg shadow-[#0ea5e9]/25" data-id="element-309">
            Request free CRM analysis now
          </a>
          <p className="mt-4 text-sm text-gray-500" data-id="element-310">
            No obligation. We analyze your current situation and show you the
            potential.
          </p>
        </motion.div>
      </div>
    </section>;
}